/**
 * Booking availability. Weekly rules are wall-clock times in the firm's timezone (minutes from
 * local midnight); slots are returned as UTC instants. Daylight-saving transitions are handled by
 * converting each local wall time through the IANA zone (Intl), so a 9:00 slot stays at 9:00
 * local on both sides of a transition, a wall time that does not exist (spring forward) is
 * skipped, and an ambiguous one (fall back) resolves to its first occurrence.
 */

export type AvailabilityRule = { weekday: number; startMin: number; endMin: number };
export type BusyInterval = { startsAt: string; endsAt: string };

export type BookingTypeConfig = {
  durationMin: number;
  bufferMin: number;
  minNoticeHours: number;
  maxDaysAhead: number;
};

type Wall = { y: number; mo: number; d: number; h: number; mi: number };

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function formatter(timezone: string): Intl.DateTimeFormat {
  let f = fmtCache.get(timezone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    fmtCache.set(timezone, f);
  }
  return f;
}

/** Local wall-clock fields of an instant in a timezone. */
export function wallTime(instantMs: number, timezone: string): Wall {
  const parts = formatter(timezone).formatToParts(new Date(instantMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute') };
}

/** Offset of the timezone from UTC at an instant, in minutes (Toronto winter: -300). */
export function offsetMinutes(instantMs: number, timezone: string): number {
  const w = wallTime(instantMs, timezone);
  const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);
  return Math.round((asUtc - Math.floor(instantMs / 60_000) * 60_000) / 60_000);
}

/**
 * The UTC instant for a local wall time, or null when it does not exist (spring-forward gap).
 * Ambiguous wall times (fall-back overlap) resolve to the earlier instant.
 */
export function zonedToUtc(date: string, minutes: number, timezone: string): Date | null {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const h = Math.floor(minutes / 60);
  const mi = minutes % 60;
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const offsets = new Set([offsetMinutes(guess - 12 * 3_600_000, timezone), offsetMinutes(guess, timezone), offsetMinutes(guess + 12 * 3_600_000, timezone)]);
  const matches: number[] = [];
  for (const off of offsets) {
    const candidate = guess - off * 60_000;
    const w = wallTime(candidate, timezone);
    if (w.y === y && w.mo === mo && w.d === d && w.h === h && w.mi === mi) matches.push(candidate);
  }
  if (matches.length === 0) return null;
  return new Date(Math.min(...matches));
}

/** YYYY-MM-DD for a local date plus `days`. */
export function addDays(date: string, days: number): string {
  const t = Date.parse(`${date}T00:00:00Z`) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

export function localDate(instantMs: number, timezone: string): string {
  const w = wallTime(instantMs, timezone);
  return `${w.y}-${String(w.mo).padStart(2, '0')}-${String(w.d).padStart(2, '0')}`;
}

export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

export type Slot = { startsAt: string; endsAt: string; localDate: string; localTime: string };

/**
 * Open slots between `fromDate` and `toDate` (inclusive local dates). Slots start every
 * `stepMin` minutes inside each rule window and must fit entirely inside it; a slot is dropped
 * when it starts before now + minimum notice, beyond max days ahead, or when it (plus the buffer
 * on either side) overlaps a busy interval.
 */
export function availableSlots(input: {
  timezone: string;
  rules: AvailabilityRule[];
  type: BookingTypeConfig;
  busy: BusyInterval[];
  now: Date;
  fromDate: string;
  toDate: string;
  stepMin?: number;
}): Slot[] {
  const { timezone, rules, type, now } = input;
  const step = input.stepMin ?? 30;
  const earliest = now.getTime() + type.minNoticeHours * 3_600_000;
  const latestDate = addDays(localDate(now.getTime(), timezone), type.maxDaysAhead);
  const buffer = type.bufferMin * 60_000;
  const busy = input.busy.map((b) => [Date.parse(b.startsAt) - buffer, Date.parse(b.endsAt) + buffer] as const);
  const out: Slot[] = [];
  for (let date = input.fromDate; date <= input.toDate && date <= latestDate; date = addDays(date, 1)) {
    const dayRules = rules.filter((r) => r.weekday === weekdayOf(date)).sort((a, b) => a.startMin - b.startMin);
    for (const rule of dayRules) {
      for (let m = rule.startMin; m + type.durationMin <= rule.endMin; m += step) {
        const start = zonedToUtc(date, m, timezone);
        if (!start) continue;
        const s = start.getTime();
        const e = s + type.durationMin * 60_000;
        if (s < earliest) continue;
        if (busy.some(([bs, be]) => s < be && e > bs)) continue;
        // The end must also be a real wall time on this day (guards slots crossing a DST jump).
        const endWall = wallTime(e, timezone);
        const expectedEnd = m + type.durationMin;
        if (endWall.h * 60 + endWall.mi !== expectedEnd % 1440) continue;
        out.push({
          startsAt: new Date(s).toISOString(),
          endsAt: new Date(e).toISOString(),
          localDate: date,
          localTime: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`,
        });
      }
    }
  }
  return out;
}

/** Whether a requested start is one of the open slots (server-side check before booking). */
export function isSlotAvailable(slots: Slot[], startsAt: string): Slot | null {
  const t = Date.parse(startsAt);
  return slots.find((s) => Date.parse(s.startsAt) === t) ?? null;
}

/** Bookings that need a reminder now: confirmed, starting within `hoursBefore`, not yet reminded. */
export function remindersDue<T extends { startsAt: string; status: string; reminderSentAt: string | null }>(bookings: T[], now: Date, hoursBefore = 24): T[] {
  const until = now.getTime() + hoursBefore * 3_600_000;
  return bookings.filter((b) => b.status === 'confirmed' && !b.reminderSentAt && Date.parse(b.startsAt) > now.getTime() && Date.parse(b.startsAt) <= until);
}

export function validateRules(rules: AvailabilityRule[]): string[] {
  const problems: string[] = [];
  for (const r of rules) {
    if (!Number.isInteger(r.weekday) || r.weekday < 0 || r.weekday > 6) problems.push('Weekday must be 0 (Sunday) to 6 (Saturday).');
    if (r.startMin < 0 || r.endMin > 1440 || r.startMin >= r.endMin) problems.push('Each window needs a start before its end, within the day.');
  }
  return problems;
}
